import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import { ArrowLeft, Link2 } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import FollowBlock from "@/components/blog/FollowBlock";
import { PostCard, PostMeta } from "@/components/blog/PostCards";
import { fetchPost } from "@/lib/blog";
import { useSeo } from "@/lib/useSeo";

const SITE = "https://anderstongroup.co.uk";

const buildJsonLd = (post) =>
  post.json_ld_schema || {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.meta_description || post.excerpt,
    image: post.image_url ? [post.image_url] : undefined,
    datePublished: post.published_at,
    dateModified: post.updated_at || post.published_at,
    author: { "@type": "Organization", name: post.author_name || "Anderston Group" },
    publisher: { "@id": `${SITE}/#organization` },
    mainEntityOfPage: `${SITE}/blog/${post.slug}`,
    inLanguage: "en-GB",
  };

export default function BlogPost() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    setData(null);
    fetchPost(slug)
      .then((d) => {
        setData(d);
        setStatus("ready");
      })
      .catch((err) => setStatus(err?.response?.status === 404 ? "missing" : "error"));
  }, [slug]);

  const post = data?.post;
  const seo = useMemo(
    () =>
      post
        ? {
            title: post.meta_title || `${post.title} | Anderston Group Journal`,
            description: post.meta_description || post.excerpt,
            path: `/blog/${post.slug}`,
            image: post.image_url,
            type: "article",
            jsonLd: buildJsonLd(post),
          }
        : {},
    [post]
  );
  useSeo(seo);

  const safeHtml = useMemo(
    () =>
      post
        ? DOMPurify.sanitize(post.content_html, {
            ADD_TAGS: ["iframe"],
            ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "target"],
          })
        : "",
    [post]
  );

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${SITE}/blog/${slug}`);
      toast.success("Link copied.");
    } catch {
      toast.error("Couldn't copy the link.");
    }
  };

  return (
    <main data-testid="blog-post-page" className="min-h-screen bg-white text-[#0A0A0A]">
      <Navbar />

      <article className="pt-[9rem] lg:pt-[11rem] pb-20 lg:pb-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Link
            to="/blog"
            data-testid="post-back-link"
            className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-black/55 hover:text-black transition-colors"
          >
            <ArrowLeft size={12} /> Journal
          </Link>

          {status === "loading" && <PostSkeleton />}
          {status === "missing" && <Missing />}
          {status === "error" && (
            <div data-testid="post-error" className="py-24 text-black/60">
              This article is unavailable right now. Please try again shortly.
            </div>
          )}

          {post && (
            <>
              {/* Header */}
              <header className="mt-10 lg:mt-14 grid grid-cols-12 gap-y-6 lg:gap-x-6">
                <div className="col-span-12 lg:col-span-9">
                  <PostMeta post={post} className="text-black/55" />
                  <h1
                    data-testid="post-title"
                    className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.025em] leading-[1.0] text-balance"
                  >
                    {post.title}
                  </h1>
                  {post.subtitle && (
                    <p
                      data-testid="post-subtitle"
                      className="mt-6 text-lg lg:text-xl text-black/60 leading-relaxed max-w-3xl"
                    >
                      {post.subtitle}
                    </p>
                  )}
                </div>
              </header>

              {/* Cover */}
              {post.image_url && (
                <div className="mt-12 lg:mt-16 aspect-[16/9] lg:aspect-[21/9] overflow-hidden">
                  <img
                    src={post.image_url}
                    alt={post.title}
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                </div>
              )}

              {/* Body */}
              <div className="mt-14 lg:mt-20 grid grid-cols-12 gap-y-8 lg:gap-x-12">
                <aside className="col-span-12 lg:col-span-3">
                  <div className="lg:sticky lg:top-40 space-y-8 text-sm">
                    <div>
                      <div className="overline">Written by</div>
                      <div className="mt-2 font-medium">{post.author_name || "Anderston Group"}</div>
                      {post.author_title && <div className="text-black/55">{post.author_title}</div>}
                    </div>
                    {post.tags?.length > 0 && (
                      <div>
                        <div className="overline">Topics</div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {post.tags.map((t) => (
                            <span key={t} className="px-2.5 py-1 text-xs border hairline text-black/70">
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <button
                      onClick={copyLink}
                      data-testid="post-copy-link"
                      className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-black/55 hover:text-black transition-colors"
                    >
                      <Link2 size={12} /> Copy link
                    </button>
                  </div>
                </aside>

                <div className="col-span-12 lg:col-span-8 lg:col-start-5">
                  <div
                    data-testid="post-body"
                    className="article-body"
                    dangerouslySetInnerHTML={{ __html: safeHtml }}
                  />

                  {post.faqs?.length > 0 && (
                    <section data-testid="post-faqs" className="mt-16 border-t hairline">
                      <div className="overline pt-8">Questions</div>
                      <dl className="mt-4">
                        {post.faqs.map((f) => (
                          <div key={f.name} className="py-6 border-b hairline">
                            <dt className="font-display text-xl lg:text-2xl tracking-tight">{f.name}</dt>
                            <dd className="mt-3 text-black/65 leading-relaxed">{f.answer}</dd>
                          </div>
                        ))}
                      </dl>
                    </section>
                  )}
                </div>
              </div>

              {/* Related */}
              {data.related?.length > 0 && (
                <section data-testid="post-related" className="mt-24 lg:mt-32">
                  <div className="overline mb-8">More from the journal</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {data.related.map((r) => (
                      <PostCard key={r.id} post={r} />
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </article>

      <FollowBlock />
      <Footer />
    </main>
  );
}

const Missing = () => (
  <div data-testid="post-missing" className="py-24 border-t hairline mt-10">
    <div className="overline">404</div>
    <h1 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight">
      That article isn't here.
    </h1>
    <p className="mt-4 text-black/60 max-w-md">
      It may have been moved or unpublished.
    </p>
    <Link
      to="/blog"
      className="mt-8 inline-flex items-center px-6 py-3.5 text-sm font-medium bg-[#0A0A0A] text-white hover:bg-black/85 transition-colors"
    >
      Back to the journal
    </Link>
  </div>
);

const PostSkeleton = () => (
  <div data-testid="post-loading" className="mt-14 animate-pulse space-y-6">
    <div className="h-3 w-56 bg-black/5" />
    <div className="h-14 w-4/5 bg-black/5" />
    <div className="h-14 w-3/5 bg-black/5" />
    <div className="h-5 w-2/3 bg-black/5" />
    <div className="aspect-[21/9] bg-black/5 mt-12" />
  </div>
);
